import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography, Dialog, DialogTitle as MuiDialogTitle, DialogContent as MuiDialogContent, DialogActions as MuiDialogActions } from '@material-ui/core';

import * as ActionsDialog from 'app/store/actions';
import * as Actions from './store/actions';
import { getErrorReportingSettings, sendErrorReport } from 'app/main/settings/store/actions/general.actions';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(() => ({
	logFont: {
		fontFamily: 'Ubuntu Mono'
	}
}));

const Logs = props => {
	const { stack, type } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const logsEndRef = useRef(null);

	const [atBottom, setAtBottom] = useState(false);
	const [firstRender, setFirstRender] = useState(true);
	const [errorReportingEnabled, setErrorReportingEnabled] = useState(false);
	const [reportSent, setReportSent] = useState(false);
	const [reportSending, setReportSending] = useState(false);
	const [showReportPreview, setShowReportPreview] = useState(false);
	const logs = useSelector(({ stacks }) => stacks.stack.logs);

	const isStackFailed = stack?.info?.state === 'failed';

	// Fetch error reporting opt-in setting on mount
	useEffect(() => {
		dispatch(getErrorReportingSettings()).then(data => {
			if (data) setErrorReportingEnabled(data.enabled || false);
		});
	}, [dispatch]);

	const buildReportPayload = () => ({
		event_type: 'error.stack_build',
		message: 'Stack build failed - ' + (stack?.name || 'unknown'),
		details: {
			heidi_version: process.env.REACT_APP_VERSION || 'unknown',
			stack_name: stack?.name || 'unknown',
			stack_id_hash: stack?.id ? btoa(stack.id).slice(0, 16) : 'unknown',
			full_log: logs ? logs.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '') : '',
			timestamp: new Date().toISOString(),
		},
	});

	const handleSendReport = async () => {
		setReportSending(true);
		try {
			const payload = buildReportPayload();
			await dispatch(sendErrorReport(payload.event_type, payload.message, payload.details));
			setReportSent(true);
			setShowReportPreview(false);
		} finally {
			setReportSending(false);
		}
	};

	const closeLogs = () => {
		dispatch(ActionsDialog.closeDialog());
		dispatch(Actions.clearLogs());
	};

	const scrollToBottom = useCallback(() => {
		setTimeout(() => logsEndRef?.current?.scrollIntoView({ behavior: 'smooth' }), 50);
	}, [logsEndRef]);

	const fetchLogs = useCallback(() => {
		dispatch(Actions.getLogsRepeat({ id: stack.id, type }))
			.then(() => atBottom && scrollToBottom())
			.catch(error => console.error("Error fetching logs:", error));
	}, [dispatch, stack.id, type, atBottom, scrollToBottom]);


	const setBottom = ({ target }) => {
		const { scrollHeight, scrollTop, clientHeight } = target;
		setAtBottom(scrollHeight - scrollTop === clientHeight);
	};

	useEffect(() => {
		if (!atBottom && firstRender) {
			setTimeout(() => logsEndRef.current.scrollIntoView(), 50);
			setFirstRender(false);
		}
	}, [firstRender, setFirstRender, atBottom]);

	useEffect(() => {
		fetchLogs();
		const intervalId = setInterval(fetchLogs, 10000);

		return () => clearInterval(intervalId);

	}, [dispatch, props, atBottom, fetchLogs]);

	return (
		<React.Fragment>
			<DialogTitle className="border-b-1">
				<div className="flex flex-row items-center justify-between">
					<span id="logs-header">{`Logs for ${props.stack.displayName || props.stack.name}`}</span>
					<Button id="scroll-btn" variant="outlined" type="button" onClick={scrollToBottom}>
						Scroll To Bottom
					</Button>
				</div>
			</DialogTitle>
			<DialogContent onScroll={e => setBottom(e)}>
				<div id="logs" className={classes.logFont} dangerouslySetInnerHTML={{ __html: logs }}></div>
				<div ref={logsEndRef} />
			</DialogContent>
			<DialogActions className="border-t-1">
				{isStackFailed && !reportSent && (
					<Button
						variant="outlined"
						color="secondary"
						onClick={() => {
							if (errorReportingEnabled) {
								handleSendReport();
							} else {
								setShowReportPreview(true);
							}
						}}
						disabled={reportSending}
					>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Error Report
					</Button>
				)}
				{reportSent && (
					<Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
						Report sent.
					</Typography>
				)}
				<Button id="logs-close-btn" onClick={closeLogs} variant="outlined">
					Close
				</Button>
			</DialogActions>

			{/* Error Report Preview Dialog */}
			<Dialog open={showReportPreview} onClose={() => setShowReportPreview(false)} maxWidth="sm" fullWidth>
				<MuiDialogTitle>Send Error Report</MuiDialogTitle>
				<MuiDialogContent>
					<Typography variant="body2" style={{ marginBottom: 12 }}>
						This will send an anonymous error report to Heidi support containing:
					</Typography>
					<Typography variant="body2" component="ul" style={{ paddingLeft: 20, marginBottom: 12 }}>
						<li>Stack name</li>
						<li>Heidi version</li>
						<li>Last 50 lines of build logs</li>
						<li>Hashed stack ID (not reversible)</li>
					</Typography>
					<Typography variant="caption" color="textSecondary">
						No IP addresses, credentials, or personal data are included. You can enable automatic error reporting in Settings &gt; General.
					</Typography>
				</MuiDialogContent>
				<MuiDialogActions>
					<Button onClick={() => setShowReportPreview(false)} variant="outlined">
						Cancel
					</Button>
					<Button onClick={handleSendReport} variant="contained" color="primary" disabled={reportSending}>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Report
					</Button>
				</MuiDialogActions>
			</Dialog>
		</React.Fragment>
	);
};

export default Logs;
